import { useState } from 'react'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Modal } from '@/components/ui/Modal'
import { Spinner } from '@/components/ui/Spinner'
import { EmptyState } from '@/components/ui/EmptyState'
import {
  useProjectContract,
  useCreateContract,
  useUpdateContract,
  useUpsertCoverage,
  useDeleteCoverage,
  useInitializeDefaultCoverages,
} from '@/hooks/useAssistance'
import {
  CONTRACT_STATUS_CONFIG,
  CONTRACT_STATUS_LABELS,
  BILLING_FREQUENCY_LABELS,
  VALID_COVERAGE_TYPES,
  COVERAGE_TYPE_LABELS,
} from '@/types/assistance'
import type {
  ContractStatus,
  BillingFrequency,
  CoverageType,
  ContractWithCoverages,
  ContractCoverageRow,
} from '@/types/assistance'
import {
  ScrollText,
  Plus,
  Pencil,
  Shield,
  CalendarDays,
  CreditCard,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import { formatDate, formatCurrency } from '@/lib/utils'
import toast from 'react-hot-toast'

interface ContractTabProps {
  projectId: string
  clientId: string
}

function ContractModal({
  isOpen,
  onClose,
  projectId,
  clientId,
  contract,
}: {
  isOpen: boolean
  onClose: () => void
  projectId: string
  clientId: string
  contract?: ContractWithCoverages | null
}) {
  const [title, setTitle] = useState(contract?.title || '')
  const [description, setDescription] = useState(contract?.description || '')
  const [amount, setAmount] = useState(contract?.amount || '')
  const [billingFrequency, setBillingFrequency] = useState<BillingFrequency>(
    (contract?.billing_frequency as BillingFrequency) || 'monthly'
  )
  const [startDate, setStartDate] = useState(contract?.start_date?.split('T')[0] || '')
  const [endDate, setEndDate] = useState(contract?.end_date?.split('T')[0] || '')
  const [autoRenew, setAutoRenew] = useState(contract?.auto_renew ?? true)
  const [contractStatus, setContractStatus] = useState<ContractStatus>(
    (contract?.status as ContractStatus) || 'active'
  )

  const createContract = useCreateContract()
  const updateContract = useUpdateContract()
  const initCoverages = useInitializeDefaultCoverages()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || !amount || !startDate) return

    try {
      if (contract) {
        await updateContract.mutateAsync({
          id: contract.id,
          updates: {
            title: title.trim(),
            description: description.trim() || null,
            amount: Number(amount),
            billing_frequency: billingFrequency,
            start_date: startDate,
            end_date: endDate || null,
            auto_renew: autoRenew,
            status: contractStatus,
          },
        })
        toast.success('Contrat mis à jour')
      } else {
        const newContract = await createContract.mutateAsync({
          project_id: projectId,
          client_id: clientId,
          title: title.trim(),
          description: description.trim() || null,
          amount: Number(amount),
          billing_frequency: billingFrequency,
          start_date: startDate,
          end_date: endDate || null,
          auto_renew: autoRenew,
        })
        await initCoverages.mutateAsync(newContract.id)
        toast.success('Contrat créé avec les couvertures par défaut')
      }
      onClose()
    } catch {
      toast.error('Erreur lors de l\'enregistrement')
    }
  }

  const isPending = createContract.isPending || updateContract.isPending || initCoverages.isPending

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={contract ? 'Modifier le contrat' : 'Nouveau contrat'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Titre du contrat"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ex: Maintenance mensuelle"
          required
        />

        <div>
          <label className="block text-sm font-medium text-brown-700 mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description du contrat..."
            rows={3}
            className="w-full px-3 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500 text-sm"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">Montant (€)</label>
            <input
              type="number"
              min="0"
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full px-3 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">Fréquence</label>
            <Select
              value={billingFrequency}
              onChange={(e) => setBillingFrequency(e.target.value as BillingFrequency)}
            >
              {Object.entries(BILLING_FREQUENCY_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            type="date"
            label="Date de début"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
          />
          <Input
            type="date"
            label="Date de fin (optionnel)"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        {contract && (
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">Statut</label>
            <Select
              value={contractStatus}
              onChange={(e) => setContractStatus(e.target.value as ContractStatus)}
            >
              {Object.entries(CONTRACT_STATUS_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </Select>
          </div>
        )}

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="auto-renew"
            checked={autoRenew}
            onChange={(e) => setAutoRenew(e.target.checked)}
            className="rounded border-beige-300 text-mint-600 focus:ring-mint-500"
          />
          <label htmlFor="auto-renew" className="text-sm text-brown-700">
            Renouvellement automatique
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          <Button type="submit" disabled={!title.trim() || !amount || !startDate || isPending}>
            {isPending ? 'Enregistrement...' : contract ? 'Modifier' : 'Créer'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

function AddCoverageModal({
  isOpen,
  onClose,
  contractId,
}: {
  isOpen: boolean
  onClose: () => void
  contractId: string
}) {
  const [label, setLabel] = useState('')
  const [coverageType, setCoverageType] = useState<CoverageType>('other')
  const upsertCoverage = useUpsertCoverage()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!label.trim()) return
    try {
      await upsertCoverage.mutateAsync({
        contract_id: contractId,
        coverage_type: coverageType,
        label: label.trim(),
        is_covered: false,
      })
      toast.success('Couverture ajoutée')
      setLabel('')
      setCoverageType('other')
      onClose()
    } catch {
      toast.error('Erreur lors de l\'ajout')
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Ajouter une couverture" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-brown-700 mb-1">Type</label>
          <Select
            value={coverageType}
            onChange={(e) => setCoverageType(e.target.value as CoverageType)}
          >
            {VALID_COVERAGE_TYPES.map((type) => (
              <option key={type} value={type}>{COVERAGE_TYPE_LABELS[type]}</option>
            ))}
          </Select>
        </div>
        <Input
          label="Libellé personnalisé"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={COVERAGE_TYPE_LABELS[coverageType]}
          required
        />
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          <Button type="submit" disabled={!label.trim() || upsertCoverage.isPending}>
            {upsertCoverage.isPending ? 'Ajout...' : 'Ajouter'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

function CoverageRow({ coverage }: { coverage: ContractCoverageRow }) {
  const upsertCoverage = useUpsertCoverage()
  const deleteCoverage = useDeleteCoverage()
  const [isCovered, setIsCovered] = useState(coverage.is_covered)
  const [maxHours, setMaxHours] = useState<number | ''>(coverage.max_hours_per_month ?? '')
  const [hourlyRate, setHourlyRate] = useState<number | ''>(coverage.hourly_rate_if_exceeded ?? '')

  const handleToggle = async () => {
    const newValue = !isCovered
    setIsCovered(newValue)
    try {
      await upsertCoverage.mutateAsync({
        id: coverage.id,
        contract_id: coverage.contract_id,
        coverage_type: coverage.coverage_type,
        label: coverage.label,
        is_covered: newValue,
      })
    } catch {
      setIsCovered(coverage.is_covered)
      toast.error('Erreur lors de la mise à jour')
    }
  }

  const handleBlur = async (field: 'max_hours_per_month' | 'hourly_rate_if_exceeded', value: number | '') => {
    const numValue = value === '' ? null : Number(value)
    if (
      (field === 'max_hours_per_month' && numValue === (coverage.max_hours_per_month ?? null)) ||
      (field === 'hourly_rate_if_exceeded' && numValue === (coverage.hourly_rate_if_exceeded ?? null))
    ) return

    try {
      await upsertCoverage.mutateAsync({
        id: coverage.id,
        contract_id: coverage.contract_id,
        coverage_type: coverage.coverage_type,
        label: coverage.label,
        [field]: numValue,
      })
    } catch {
      toast.error('Erreur lors de la mise à jour')
    }
  }

  const handleDelete = async () => {
    try {
      await deleteCoverage.mutateAsync({ id: coverage.id, contractId: coverage.contract_id })
      toast.success('Couverture supprimée')
    } catch {
      toast.error('Erreur lors de la suppression')
    }
  }

  return (
    <tr className="border-b border-beige-100 last:border-0">
      <td className="px-4 py-3 text-sm text-brown-700 font-medium">{coverage.label}</td>
      <td className="px-4 py-3 text-center">
        <button
          type="button"
          onClick={handleToggle}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            isCovered ? 'bg-mint-500' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              isCovered ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </td>
      <td className="px-4 py-3">
        <input
          type="number"
          step="0.5"
          min="0"
          value={maxHours}
          onChange={(e) => setMaxHours(e.target.value === '' ? '' : Number(e.target.value))}
          onBlur={() => handleBlur('max_hours_per_month', maxHours)}
          className="w-20 px-2 py-1 border border-beige-300 rounded text-sm text-center focus:ring-1 focus:ring-mint-500 focus:border-mint-500"
          placeholder="-"
        />
      </td>
      <td className="px-4 py-3">
        <input
          type="number"
          step="1"
          min="0"
          value={hourlyRate}
          onChange={(e) => setHourlyRate(e.target.value === '' ? '' : Number(e.target.value))}
          onBlur={() => handleBlur('hourly_rate_if_exceeded', hourlyRate)}
          className="w-20 px-2 py-1 border border-beige-300 rounded text-sm text-center focus:ring-1 focus:ring-mint-500 focus:border-mint-500"
          placeholder="-"
        />
      </td>
      <td className="px-4 py-3 text-center">
        <button
          type="button"
          onClick={handleDelete}
          disabled={deleteCoverage.isPending}
          className="text-red-400 hover:text-red-600 transition-colors"
          title="Supprimer"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </td>
    </tr>
  )
}

export function ContractTab({ projectId, clientId }: ContractTabProps) {
  const { data: contract, isLoading } = useProjectContract(projectId)
  const [showModal, setShowModal] = useState(false)
  const [showAddCoverage, setShowAddCoverage] = useState(false)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!contract) {
    return (
      <Card padding="lg">
        <EmptyState
          icon={<ScrollText className="w-12 h-12" />}
          title="Aucun contrat"
          description="Ce projet n'a pas encore de contrat de maintenance."
          action={
            <Button
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => setShowModal(true)}
            >
              Créer un contrat
            </Button>
          }
        />
        <ContractModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          projectId={projectId}
          clientId={clientId}
        />
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Contract summary */}
      <Card>
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <ScrollText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-brown-900">{contract.title}</h3>
                <Badge
                  variant={CONTRACT_STATUS_CONFIG[contract.status as ContractStatus]?.color}
                  size="sm"
                >
                  {CONTRACT_STATUS_CONFIG[contract.status as ContractStatus]?.label}
                </Badge>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              leftIcon={<Pencil className="w-4 h-4" />}
              onClick={() => setShowModal(true)}
            >
              Modifier
            </Button>
          </div>

          {contract.description && (
            <p className="text-sm text-brown-600 mb-4">{contract.description}</p>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-brown-400" />
              <div>
                <p className="text-xs text-brown-500">Montant</p>
                <p className="text-sm font-medium text-brown-900">
                  {formatCurrency(contract.amount)}/{BILLING_FREQUENCY_LABELS[contract.billing_frequency as BillingFrequency] || contract.billing_frequency}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-brown-400" />
              <div>
                <p className="text-xs text-brown-500">Début</p>
                <p className="text-sm font-medium text-brown-900">
                  {formatDate(contract.start_date)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-brown-400" />
              <div>
                <p className="text-xs text-brown-500">Fin</p>
                <p className="text-sm font-medium text-brown-900">
                  {contract.end_date ? formatDate(contract.end_date) : 'Indéterminée'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-brown-400" />
              <div>
                <p className="text-xs text-brown-500">Renouvellement</p>
                <p className="text-sm font-medium text-brown-900">
                  {contract.auto_renew ? 'Automatique' : 'Manuel'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Coverages section */}
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-mint-600" />
              <h3 className="font-semibold text-brown-900">Couvertures</h3>
            </div>
            <Button
              size="sm"
              variant="outline"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => setShowAddCoverage(true)}
            >
              Ajouter
            </Button>
          </div>

          {contract.contract_coverages && contract.contract_coverages.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-beige-200">
                    <th className="px-4 py-2 text-left text-xs font-medium text-brown-600 uppercase">
                      Type
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-brown-600 uppercase">
                      Couvert
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-brown-600 uppercase">
                      Heures max/mois
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-brown-600 uppercase">
                      Taux horaire (€)
                    </th>
                    <th className="px-4 py-2 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {contract.contract_coverages.map((cov: ContractCoverageRow) => (
                    <CoverageRow key={cov.id} coverage={cov} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-brown-500">Aucune couverture définie.</p>
          )}
        </div>
      </Card>

      {/* Edit modal */}
      <ContractModal
        key={contract.id}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        projectId={projectId}
        clientId={clientId}
        contract={contract}
      />

      {/* Add coverage modal */}
      <AddCoverageModal
        isOpen={showAddCoverage}
        onClose={() => setShowAddCoverage(false)}
        contractId={contract.id}
      />
    </div>
  )
}

export default ContractTab
